
import _isBefore from "date-fns/isBefore"
import _isAfter from "date-fns/isAfter"

import { TFunction } from "i18next";
import { DATE_FORMAT, TIME_FORMAT } from "../../constants/formats"
import { PickerUtils } from "./PickerUtils";

interface IValidateRangeOptions {
    minDate: Date | undefined,
    maxDate: Date | undefined,
    format: string,
    validateInput: (inputValue: string) => boolean
};

interface IValidateDateRangeOptions {
    minDate: Date | undefined,
    maxDate: Date | undefined
};

interface IValidateTimeRangeOptions {
    minDate: Date | undefined,
    maxDate: Date | undefined
};

export enum ERangeError {
    MaxDateBoundary = "MaxDateBoundary",
    MinDateBoundary = "MinDateBoundary",
    InvalidInput = "InvalidInput",
    InvalidDate = "InvalidDate",
};

const validateRange = (inputValue: string, options: IValidateRangeOptions): ERangeError | undefined => {
    try {
        const { format, validateInput, minDate, maxDate } = options

        const inputTextSeemsCompleteDate = validateInput(inputValue);

        if (!inputTextSeemsCompleteDate) {
            return ERangeError.InvalidInput;
        };

        const referenceDate = PickerUtils.resetTime(new Date());
        const newDate = PickerUtils.parseString(inputValue, format, referenceDate);
        if (!newDate) {
            throw Error("Failed to parse date");
        };

        const beforeMinDate = minDate && newDate ? _isBefore(newDate, minDate) : false;

        if (beforeMinDate) {
            return ERangeError.MinDateBoundary;
        };

        const dateAfterMaxDate = maxDate && newDate ? _isAfter(newDate, maxDate) : false;

        if (dateAfterMaxDate) {
            return ERangeError.MaxDateBoundary;
        };

    } catch (error) {
       // console.info("[validateRange]", { error })
        return ERangeError.InvalidDate
    };
};

const PickerValidator = {
    validateRange,

    Date: {

        validateInput: (inputValue: string, strict = false): boolean => {
            let [day, month, year] = inputValue.split(".");
            const inputTextSeemsCompleteDate = day && month && (year && year.length === 4);
            const strictInputTextSeemsCompleteDate = day && day.length === 2 && month && month.length === 2 && (year && year.length === 4);
            return Boolean(strict ? strictInputTextSeemsCompleteDate : inputTextSeemsCompleteDate);
        },

        validateRange: (inputValue: string, options: IValidateDateRangeOptions): ERangeError | undefined => {


            return validateRange(inputValue, {
                minDate: options.minDate ? PickerUtils.resetTime(PickerUtils.ensureFreshDateInstance(options.minDate) as any) : undefined,
                maxDate: options.maxDate ? PickerUtils.resetTime(PickerUtils.ensureFreshDateInstance(options.maxDate) as any) : undefined,
                format: DATE_FORMAT,
                validateInput: PickerValidator.Date.validateInput
            });
        },

        getRangeErrorMessage: (status: ERangeError, options: IValidateDateRangeOptions, t: TFunction) => {
            const { minDate, maxDate } = options;
            const texts = {
                maxDate: t("Suurin sallittu päivämäärä "),
                minDate: t("Pienin sallittu päivämäärä "),
                invalidDate: t("Virheellinen päivämäärä")
            }
            const errorMessage = {
                [ERangeError.InvalidInput]: undefined,
                [ERangeError.MaxDateBoundary]: texts.maxDate + (maxDate ? PickerUtils.formatDate(maxDate, DATE_FORMAT) : ""),
                [ERangeError.MinDateBoundary]: texts.minDate + (minDate ? PickerUtils.formatDate(minDate, DATE_FORMAT) : ""),
                [ERangeError.InvalidDate]: texts.invalidDate,
            }[status];

            return errorMessage;
        }
    },

    Time: {
        validateInput: (inputValue: string, strict = false): boolean => {
            let [hour, minute] = inputValue.split(":");
            const inputTextSeemsCompleteTime = hour && minute;
            const strictInputTextSeemsCompleteTime = hour && hour.length === 2 && minute && minute.length === 2;
            return Boolean(strict ? strictInputTextSeemsCompleteTime : inputTextSeemsCompleteTime);
        },

        validateRange: (inputValue: string, options: IValidateTimeRangeOptions): ERangeError | undefined => {
            return validateRange(inputValue, {
                minDate: options.minDate,
                maxDate: options.maxDate,
                format: TIME_FORMAT,
                validateInput: PickerValidator.Time.validateInput
            });
        },

        getRangeErrorMessage: (status: ERangeError, options: IValidateTimeRangeOptions, t: TFunction) => {
            const { minDate, maxDate } = options;
            const texts = {
                maxTime: t("Suurin sallittu aika"),
                minTime: t("Pienin sallittu aika "),
                invalidTime: t("Virheellinen päivämäärä")
            }
            const errorMessage = {
                [ERangeError.InvalidInput]: undefined,
                [ERangeError.MaxDateBoundary]: texts.maxTime + " " + (maxDate ? PickerUtils.formatDate(maxDate, TIME_FORMAT) : ""),
                [ERangeError.MinDateBoundary]: texts.minTime + " " + (minDate ? PickerUtils.formatDate(minDate, TIME_FORMAT) : ""),
                [ERangeError.InvalidDate]: texts.invalidTime,
            }[status];

            return errorMessage;
        }
    }
}



export default PickerValidator;